// https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver
// https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver

export default class LayerObserver {
    constructor(options) {
        this.isScrollingElement = options?.isScrollingElement || false;
        this.isStickyTop = options?.isStickyTop || false;
        this.isStickyBottom = options?.isStickyBottom || false;
        this.domElement = options?.domElement || null;
        this.id = options.id;
        this.manager = options.manager;
        this.observers = options.observers;

        this.isInView = false;
        this.needsResize = true;

        this.box = {
            width: 0,
            height: 0,
            top: 0,
            left: 0,
            scrollingTop: 0,
        };

        if (this.domElement) this.observeElement(this.domElement);
    }

    observeElement(domElement, updateFunction) {
        if (this.domElement) this.unobserveElement();

        if (!domElement) return;

        this.observers.resizeMap.set(domElement, this.resizeCallback.bind(this));
        this.observers.resize.observe(domElement, this.observers.resizeConfig);

        this.observers.mutationMap.set(domElement, this.mutationCallback.bind(this));
        this.observers.mutation.observe(domElement, this.observers.mutationConfig);

        this.observers.intersectionMap.set(domElement, this.intersectionCallback.bind(this));
        this.observers.intersection.observe(domElement);

        this.domElement = domElement;

        this.updateLayerFunction = updateFunction;

        this.resize(true);
    }

    intersectionCallback(isIntersecting) {
        if (isIntersecting) {
            this.isInView = true;
            this.resize();
        } else {
            this.isInView = false;
        }
    }

    mutationCallback() {
        this.resize();
    }

    resizeCallback() {
        this.resize();
    }

    unobserveElement(domElement) {
        const element = domElement || this.domElement;
        if (!element) return;

        this.observers.resizeMap.delete(element);
        this.observers.mutationMap.delete(element);
        this.observers.intersectionMap.delete(element);

        this.observers.resize.unobserve(element);
        this.observers.mutation.disconnect();
        this.observers.intersection.unobserve(element);

        this.isInView = false;

        this.domElement = null;
    }

    resize(forceNeedsResize = false) {
        // Make sure resize is triggered on new page
        if (forceNeedsResize) this.needsResize = true;
        if (!this.needsResize) return;
        if (!this.domElement) return;

        if (!this.isInView) return;

        this.needsResize = false;

        requestAnimationFrame(() => {
            this.needsResize = true;
            if (!this.domElement) return;

            const box = this.domElement.getBoundingClientRect();
            this.box.top = box.top;
            this.box.width = box.width;
            this.box.height = box.height;
            this.box.left = box.left;
            this.box.stickyTop = box.top;
            this.box.scrollingTop = this.box.top + this.manager.getScroll();
            this.updateLayerFunction && this.updateLayerFunction(this.box);
        });
    }

    resizeNotFrameBased() {
        if (!this.domElement) return;
        if (!this.isInView) return;

        const box = this.domElement.getBoundingClientRect();
        this.box.top = box.top;
        this.box.width = box.width;
        this.box.height = box.height;
        this.box.left = box.left;
        this.box.stickyTop = box.top;
        this.box.scrollingTop = this.box.top + this.manager.scrollY;
        this.updateLayerFunction && this.updateLayerFunction(this.box);
    }

    update(needUpdate) {
        if (this.isScrollingElement && needUpdate) this.checkDomPosition();
    }

    checkDomPosition() {
        if (this.isInView) {
            this.box.top = this.box.scrollingTop - this.manager.scrollY;
        }
    }

    delete() {
        this.unobserveElement();
        // this.resizeObserver.disconnect();
        // delete this.resizeObserver;
        // this.mutationObserver.disconnect();
        // delete this.mutationObserver;
        // this.intersectionObserver.disconnect();
        // delete this.intersectionObserver;
    }
}
